<template>
  <GloboGraphicsModulo
    icon="mdi-format-float-left"
    title="Templates e cenas"
    collapsible
    :offline="offline"
  >
    <!-- Subheader: search field (inline accent icon) + import / config buttons -->
    <template #subheader>
      <div class="gg-cenas-sub">
        <div
          class="gg-cenas-search"
          :class="savedScenes.length === 0 ? '--disabled' : ''"
        >
          <input
            v-model="search"
            :disabled="savedScenes.length === 0"
            type="text"
            class="gg-cenas-search__input"
            :class="savedScenes.length === 0 ? '--disabled' : ''"
            :placeholder="
              savedScenes.length === 0
                ? 'Nenhum template ou cena disponível'
                : 'Digite para buscar templates ou cenas'
            "
            aria-label="Buscar templates ou cenas"
          />
          <span
            class="gg-cenas-search__icon"
            :class="savedScenes.length === 0 ? '--disabled' : ''"
            aria-hidden="true"
          >
            <v-icon size="20">mdi-magnify</v-icon>
          </span>
        </div>
        <button
          type="button"
          class="gg-cenas-iconbtn"
          title="Importar templates e cenas"
          @click="showImportDrawer = true"
        >
          <v-icon size="24">mdi-folder-upload</v-icon>
        </button>
        <v-menu offset-y nudge-bottom="4" content-class="gg-select-menu">
          <template v-slot:activator="{ on, attrs }">
            <button
              type="button"
              class="gg-cenas-iconbtn"
              :class="savedScenes.length === 0 ? '--empty' : ''"
              :title="savedScenes.length === 0 ? '' : 'Mais ações'"
              v-bind="attrs"
              v-on="on"
              :disabled="savedScenes.length === 0"
            >
              <v-icon size="24">mdi-dots-vertical</v-icon>
            </button>
          </template>
          <v-list dense>
            <v-list-item @click="showBrowseDrawer = true">
              <v-icon small class="mr-2">mdi-folder-search</v-icon>
              <span class="gg-select__option">Navegar Graphic Hub</span>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </template>

    <!-- Imported templates + cenas (flat list; thumbnail + name) -->
    <div v-if="filteredScenes.length" class="gg-cenas-list">
      <div
        v-for="scene in filteredScenes"
        :key="scene.id_gh_cena"
        class="gg-cena-row"
        :class="{
          'gg-cena-row--onair': isSceneOnAir(scene),
          'gg-cena-row--selected':
            !isSceneOnAir(scene) && selectedId === scene.id_gh_cena,
        }"
        @click="handleSelectScene(scene)"
      >
        <div class="gg-cena-row__thumb">
          <img
            v-if="sceneImageUrl(scene) && !thumbError[scene.id_gh_cena]"
            :src="sceneImageUrl(scene)"
            alt=""
            @error="onThumbError(scene.id_gh_cena)"
          />
          <v-icon v-else size="20" class="gg-cena-row__thumb-icon"
            >mdi-image-outline</v-icon
          >
        </div>
        <span class="gg-cena-row__name">{{
          scene.dsc_nome_cena || "Cena sem nome"
        }}</span>
        <span
          v-if="isSceneOnAir(scene)"
          class="gg-cena-row__badge gg-cena-row__badge--onair"
        >
          <v-icon size="10" class="mr-1">mdi-circle</v-icon>ON AIR
        </span>
        <span
          v-else-if="selectedId === scene.id_gh_cena"
          class="gg-cena-row__badge gg-cena-row__badge--pvw"
        >
          <v-icon size="10" class="mr-1">mdi-circle</v-icon>PVW
        </span>
        <button
          type="button"
          class="gg-cena-row__delete"
          title="Excluir"
          @click.stop="handleDeleteScene(scene)"
        >
          <v-icon size="16">mdi-delete-outline</v-icon>
        </button>
      </div>
    </div>
    <div v-else class="gg-empty-box" :class="{ 'gg-empty-box--actions': !search }">
      <span class="gg-empty-box__text">{{
        search
          ? "Nenhum template ou cena para a busca."
          : "Para começar, importe templates e cenas disponíveis"
      }}</span>
      <div v-if="!search" class="gg-empty-box__actions">
        <button
          type="button"
          class="gg-empty-btn gg-empty-btn--outlined"
          @click="showImportDrawer = true"
        >
          Importar
        </button>
      </div>
    </div>

    <!-- Import drawer — the Vizrt template catalog (slides in from the right). -->
    <GloboGraphicsImportDrawer v-model="showImportDrawer" :cod-producao="cod_producao" />

    <!-- Browse GH drawer (slides in from the right). -->
    <v-navigation-drawer
      ref="browseDrawer"
      v-model="showBrowseDrawer"
      right
      temporary
      fixed
      width="960"
      role="dialog"
      aria-modal="true"
      aria-labelledby="gg-drawer-browse-title"
      class="gg-drawer"
    >
      <div class="gg-drawer__header">
        <span id="gg-drawer-browse-title" class="gg-drawer__title">
          <v-icon size="20" class="mr-2">mdi-folder-search-outline</v-icon>
          Navegar Graphic Hub
        </span>
        <button
          type="button"
          class="gg-drawer__close"
          aria-label="Fechar"
          @click="showBrowseDrawer = false"
        >
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </div>
      <div class="gg-drawer__body gg-drawer__body--flush">
        <GloboGraphicsGhColumns v-if="showBrowseDrawer" />
      </div>
    </v-navigation-drawer>

    <!-- Link Project Dialog -->
    <GloboGraphicsLinkProject
      v-if="showLinkProject"
      :visible="showLinkProject"
      :cod_producao="cod_producao"
      @close="showLinkProject = false"
    />

    <!-- Delete confirmation -->
    <ModalConfirmAction
      :visible="showDeleteSceneDialog"
      title="Excluir cena"
      message="Deseja excluir esta cena salva?"
      confirm-text="Excluir"
      confirm-color="red"
      @confirm="confirmDeleteScene"
      @cancel="showDeleteSceneDialog = false"
    />
  </GloboGraphicsModulo>
</template>

<script>
/**
 * Templates e cenas block — the production's imported templates + saved cenas,
 * shown as a flat searchable list. The subheader button and kebab open right-side
 * drawers:
 *   - Importar (folder-upload) → GloboGraphicsImportDrawer (Vizrt catalog)
 *   - Navegar Graphic Hub      → GloboGraphicsGhColumns (GH folders, cascading)
 * Selecting a row emits `select-scene` so the view can drive the Preview + Editor
 * blocks (same surfaces the playlist items use).
 */
import { mapActions, mapGetters } from "vuex";
import { getVizrtThumbnailUrl } from "@/store/modules/globographics";
import GloboGraphicsModulo from "./GloboGraphicsModulo.vue";
import GloboGraphicsLinkProject from "./GloboGraphicsLinkProject.vue";
import GloboGraphicsImportDrawer from "./GloboGraphicsImportDrawer.vue";
import GloboGraphicsGhColumns from "./cenas/GloboGraphicsGhColumns.vue";
import { trapTab, focusFirstWithin, restoreFocus } from "@/utils/ggFocusTrap";

export default {
  name: "GloboGraphicsCenasDisponiveis",
  components: {
    GloboGraphicsModulo,
    GloboGraphicsLinkProject,
    GloboGraphicsImportDrawer,
    GloboGraphicsGhColumns,
    ModalConfirmAction: () => import("@/components/Modal/ModalConfirmAction.vue"),
  },
  props: {
    cod_producao: { type: Number, required: true },
    selectedId: { type: [Number, String], default: null },
    offline: { type: Boolean, default: false },
  },
  // This drawer owns its open state (v-model) and is not this component's root
  // element, so it cannot use the shared ggDrawerA11y mixin — that one closes by
  // emitting `input` and traps against `this.$el`. It shares the trap itself
  // (@/utils/ggFocusTrap) rather than carrying a second copy: VNavigationDrawer has
  // no keydown handling at all, so without it Esc does nothing and Tab walks out of
  // the drawer into the page behind.
  watch: {
    showBrowseDrawer(open) {
      if (open) {
        document.addEventListener("keydown", this.onBrowseDrawerKeydown);
        this.$nextTick(() => {
          this.browseReturnFocus = focusFirstWithin(this.$refs.browseDrawer?.$el);
        });
      } else {
        document.removeEventListener("keydown", this.onBrowseDrawerKeydown);
        restoreFocus(this.browseReturnFocus);
        this.browseReturnFocus = null;
      }
    },
  },
  data() {
    return {
      search: "",
      // Element that had focus when the browse drawer opened, so it can be handed back.
      browseReturnFocus: null,
      showImportDrawer: false,
      showBrowseDrawer: false,
      showLinkProject: false,
      showDeleteSceneDialog: false,
      pendingDeleteScene: null,
      thumbError: {},
    };
  },
  computed: {
    ...mapGetters({
      savedScenes: "globographics/getterSavedScenes",
      activePlaylist: "playout/getterActivePlaylist",
      onAirItem: "playout/getterOnAirItem",
    }),
    filteredScenes() {
      const list = this.savedScenes || [];
      const q = this.search.trim().toLowerCase();
      if (!q) return list;
      return list.filter((s) => (s.dsc_nome_cena || "").toLowerCase().includes(q));
    },
    // The gh-cena id of whatever is on air via the active playlist. Deriving the
    // Salvas on-air state from this (the SAME playlist source the playlist rows use)
    // keeps both surfaces in lockstep — otherwise the two lists read on-air from
    // independently-refreshed stores and light/clear the live pipe out of sync.
    onAirGhCenaId() {
      const it = this.onAirItem;
      if (!it) return null;
      return it.id_gh_cena != null
        ? it.id_gh_cena
        : (it.cena && it.cena.id_gh_cena) || null;
    },
  },
  beforeDestroy() {
    // Listener is on document, so it outlives this component unless removed.
    document.removeEventListener("keydown", this.onBrowseDrawerKeydown);
  },
  methods: {
    onBrowseDrawerKeydown(event) {
      if (event.key === "Escape") {
        this.showBrowseDrawer = false;
        return;
      }
      trapTab(this.$refs.browseDrawer?.$el, event);
    },
    ...mapActions({
      deleteCena: "globographics/deleteCena",
      clearPreviewSelection: "playout/clearPreviewSelection",
    }),
    // On air when this cena is the one the active playlist is airing (kept in sync
    // with the playlist row). Falls back to the cena's own server flag only when no
    // playlist is active, so a directly-exhibited cena still reads correctly.
    isSceneOnAir(scene) {
      if (this.activePlaylist)
        return this.onAirGhCenaId != null && scene.id_gh_cena === this.onAirGhCenaId;
      return !!scene.flg_no_ar;
    },
    // The template's catalog thumbnail — the same image the playlist rows fall back
    // to for a native item.
    sceneImageUrl(scene) {
      if (!scene) return null;
      return getVizrtThumbnailUrl(scene.dsc_vizrt_template_id);
    },
    onThumbError(id) {
      this.$set(this.thumbError, id, true);
    },
    // Select a row → Preview + Editor (handled at the view level).
    handleSelectScene(scene) {
      this.$emit("select-scene", scene);
    },
    // Import a template into the block = create a cena from it with empty values
    // (no editor step — the operator edits it afterwards in the Editor block). The
    // drawer stays open so several templates can be imported in a row.
    handleDeleteScene(scene) {
      this.pendingDeleteScene = scene;
      this.showDeleteSceneDialog = true;
    },
    async confirmDeleteScene() {
      const scene = this.pendingDeleteScene;
      this.showDeleteSceneDialog = false;
      this.pendingDeleteScene = null;
      try {
        await this.deleteCena({
          idGhCena: scene.id_gh_cena,
          codProducao: this.cod_producao,
        });
        // A deleted cena must not stay in Preview/Editor. Clearing is required
        // rather than automatic: deleteCena refreshes savedScenes, and once the
        // cena is gone from there getterPreviewItem falls back to the stored
        // selection and would keep serving the removed cena. Guarded so only the
        // deleted one is cleared — selectedId is null while a playlist item is
        // previewed, so deleting an unrelated cena never disturbs that selection.
        if (this.selectedId === scene.id_gh_cena) this.clearPreviewSelection();
        this.$toast.success("Cena excluída");
      } catch (error) {
        const msg = error.response?.data?.error || "Erro ao excluir cena";
        this.$toast.error(msg);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// ── Subheader: search field + accent icon buttons ───────────────────────
.gg-cenas-sub {
  display: flex;
  align-items: center;
  gap: var(--sc-space-4);
  width: 100%;
}

.gg-cenas-search {
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
  height: 56px;
  padding: 0 var(--sc-space-2) 0 var(--sc-space-3);
  background: var(--sc-gg-surface-2);
  border: 1px solid var(--sc-gg-border-100);
  border-radius: var(--sc-radius-md);

  &.--disabled {
    background: var(--sc-gg-surface-3);
  }

  &:focus-within {
    border-color: var(--sc-gg-accent);
  }
}

.gg-cenas-search__input {
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  background: transparent;
  color: var(--sc-gg-text-primary);
  font-size: 16px;
  outline: none;

  &::placeholder {
    color: var(--sc-gg-text-tertiary);
  }

  &.--disabled {
    &::placeholder {
      color: var(--sc-gg-disabled);
    }
  }
}

.gg-cenas-search__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .v-icon {
    color: var(--sc-gg-accent) !important;
  }

  &.--disabled {
    .v-icon {
      color: var(--sc-gg-disabled) !important;
    }
  }
}

.gg-cenas-iconbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border: 1px solid var(--sc-gg-accent);
  border-radius: var(--sc-radius-lg);
  color: var(--sc-gg-accent);
  transition: background var(--sc-duration-fast) var(--sc-easing-standard);

  .v-icon {
    color: var(--sc-gg-accent) !important;
  }

  &:hover {
    background: rgba(var(--sc-gg-accent-rgb), 0.12);
  }

  &.--empty {
    .v-icon {
      color: var(--sc-gg-disabled) !important;
    }

    &:hover {
    background: transparent;
    }
  }
}

// ── Flat list of imported templates / cenas ─────────────────────────────
.gg-cenas-list {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.gg-cena-row {
  display: flex;
  align-items: center;
  gap: var(--sc-space-6);
  padding: var(--sc-space-2) var(--sc-space-4);
  // Rows sit on the module surface — only the separator divides them.
  background: var(--sc-gg-surface-2);
  // Leading pipe always reserved (transparent) so it recolours without shifting
  // the thumbnail/name. Red when the cena is on air, green when it's previewed.
  border-left: 3px solid transparent;
  border-bottom: 2px solid rgba(77, 78, 81, 0.3);
  cursor: pointer;
  transition: background-color var(--sc-duration-fast) var(--sc-easing-standard);

  &:hover {
    background: var(--sc-gg-surface-1);
  }

  &--onair {
    border-left-color: var(--sc-gg-error);
  }

  &--selected {
    border-left-color: var(--sc-gg-success);
  }
}

.gg-cena-row__thumb {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 88px;
  height: 64px;
  flex-shrink: 0;
  background: rgba(0, 0, 0, 0.25);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.gg-cena-row__thumb-icon {
  color: var(--sc-gg-text-tertiary) !important;
}

.gg-cena-row__name {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12px;
  font-weight: var(--sc-weight-medium);
  line-height: 16px;
  letter-spacing: 0.06px;
  color: var(--sc-gg-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gg-cena-row__badge {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: var(--sc-weight-medium);
  line-height: 16px;
  letter-spacing: 0.5px;
  white-space: nowrap;

  .v-icon {
    color: inherit !important;
  }
}

.gg-cena-row__badge--onair {
  color: var(--sc-gg-critical);
}

.gg-cena-row__badge--pvw {
  color: var(--sc-gg-success);
}

.gg-cena-row__delete {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: var(--sc-radius-md);
  color: var(--sc-gg-text-tertiary);
  opacity: 0;
  transition: opacity var(--sc-duration-fast) var(--sc-easing-standard);

  .v-icon {
    color: inherit !important;
  }

  &:hover {
    color: var(--sc-gg-critical);
  }
}

.gg-cena-row:hover .gg-cena-row__delete {
  opacity: 1;
}

// ── Right-side drawers (gavetas) ─────────────────────────────────────────
.gg-drawer ::v-deep {
  background: var(--sc-gg-surface-1) !important;
  border-left: 1px solid var(--sc-gg-border-100);
}

.gg-drawer__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--sc-space-4) var(--sc-space-6);
  border-bottom: 1px solid var(--sc-gg-border-100);
}

.gg-drawer__title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: var(--sc-weight-semi);
  color: var(--sc-gg-text-title);

  .v-icon {
    color: var(--sc-gg-text-strong) !important;
  }
}

.gg-drawer__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--sc-radius-md);
  color: var(--sc-gg-text-strong);

  .v-icon {
    color: inherit !important;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }
}

.gg-drawer__body {
  padding: var(--sc-space-6);
  height: calc(100% - 65px);
  overflow-y: auto;
}

.gg-drawer__body--flush {
  padding: var(--sc-space-3);
  overflow: hidden;
}
</style>
